import React, { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SaveIcon from '@mui/icons-material/Save';
import SyncIcon from '@mui/icons-material/Sync';
import SendIcon from '@mui/icons-material/Send';
import LogoutIcon from '@mui/icons-material/Logout';
import { useRules } from '../context/RuleContext';
import { saveAs } from 'file-saver';

export default function Header({ user, onLogout }) {
  const { exportToXml, fetchFromDb, saveToDb, submitForApproval } = useRules();
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [submitDescription, setSubmitDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleDownload = () => {
    const xml = exportToXml();
    const blob = new Blob([xml], { type: 'application/xml' });
    saveAs(blob, `FinalAllTravomint-${new Date().toISOString().slice(0, 10)}.xml`);
  };

  const handleSync = async () => { setSyncing(true); await fetchFromDb(); setSyncing(false); };
  const handleSave = async () => { setSaving(true); await saveToDb(); setSaving(false); };

  const handleSubmitForApproval = async () => {
    if (!submitDescription.trim()) return;
    setSubmitting(true);
    await submitForApproval(submitDescription.trim());
    setSubmitting(false);
    setSubmitDialogOpen(false);
    setSubmitDescription('');
  };

  return (
    <>
      <AppBar position="static" color="default" elevation={1} sx={{ bgcolor: 'white' }}>
        <Toolbar>
          <Chip label={`${user?.name || 'User'} (${user?.role})`} size="small"
            color={user?.role === 'ADMIN' ? 'error' : 'primary'} variant="outlined" />
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="outlined"
              startIcon={syncing ? <CircularProgress size={14} /> : <SyncIcon />}
              onClick={handleSync} disabled={syncing}>
              Sync
            </Button>

            {user?.role === 'ADMIN' ? (
              <Button size="small" variant="contained" color="success"
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                onClick={handleSave} disabled={saving}>
                Save to DB
              </Button>
            ) : (
              <Button size="small" variant="contained" color="info"
                startIcon={<SendIcon />}
                onClick={() => setSubmitDialogOpen(true)}>
                Submit for Approval
              </Button>
            )}

            <Button size="small" variant="contained" startIcon={<CloudDownloadIcon />} onClick={handleDownload}>
              Download
            </Button>
            <Button size="small" variant="outlined" color="error" startIcon={<LogoutIcon />} onClick={onLogout}>
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Submit for Approval Dialog */}
      <Dialog open={submitDialogOpen} onClose={() => setSubmitDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Submit Changes for Approval</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your current changes will be sent to the admin for review. Once approved, they will be saved to the master database file.
          </Typography>
          <TextField
            fullWidth label="Description of changes" value={submitDescription}
            onChange={(e) => setSubmitDescription(e.target.value)}
            placeholder="e.g. Added new AI discount rules for AE-IN, Extended dates for carrier group 9"
            multiline rows={3}
            helperText="Describe what you changed so admin can review"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubmitDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="info" startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
            onClick={handleSubmitForApproval} disabled={submitting || !submitDescription.trim()}>
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
